import { useOrientation } from '../hooks/useOrientation';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Orientation guard overlay.
 * Shows a rotate-device message when in portrait mode on mobile.
 */
export function OrientationGuard() {
  const { isPortrait, isMobile } = useOrientation();
  const showOverlay = isMobile && !isPortrait;

  return (
    <AnimatePresence>
      {showOverlay && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-game-bg/95 backdrop-blur-xl"
          style={{ zIndex: 'var(--z-orientation, 100)' }}
        >
          {/* Animated rotate icon */}
          <motion.div
            animate={{ rotate: [90, 0, 0, 90] }}
            transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: 'easeInOut',
              times: [0, 0.4, 0.6, 1],
            }}
            className="mb-8"
          >
            <svg
              width="80"
              height="80"
              viewBox="0 0 80 80"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="drop-shadow-[0_0_20px_rgba(124,58,237,0.5)]"
            >
              {/* Phone outline */}
              <rect
                x="20"
                y="10"
                width="40"
                height="60"
                rx="6"
                stroke="url(#gradient)"
                strokeWidth="2.5"
                fill="rgba(124, 58, 237, 0.1)"
              />
              {/* Screen */}
              <rect
                x="24"
                y="18"
                width="32"
                height="44"
                rx="2"
                fill="rgba(124, 58, 237, 0.15)"
              />
              {/* Home button */}
              <circle cx="40" cy="68" r="3" fill="rgba(124, 58, 237, 0.4)" />
              <defs>
                <linearGradient id="gradient" x1="20" y1="10" x2="60" y2="70">
                  <stop offset="0%" stopColor="#7c3aed" />
                  <stop offset="100%" stopColor="#f06595" />
                </linearGradient>
              </defs>
            </svg>
          </motion.div>

          {/* Text */}
          <motion.h2
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="font-display text-2xl font-bold text-gradient mb-3"
          >
            Rotate Your Device
          </motion.h2>
          <motion.p
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-surface-400 text-base"
          >
            Please switch to portrait (vertical) mode to play.
          </motion.p>

          {/* Decorative glow orbs */}
          <div className="absolute top-1/4 left-1/4 w-32 h-32 rounded-full bg-primary-600/10 blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-40 h-40 rounded-full bg-accent-600/10 blur-3xl" />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
